import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { del, get, post, put } from "../services/apiClient";

export type FeatureCategory =
  | "BUSINESS"
  | "BOOKINGS"
  | "CUSTOMER_ENGAGEMENT"
  | "ANALYTICS"
  | "SUPPORT"
  | "MARKETING"
  | "STAFF"
  | "ADMIN"
  | "REPORTS";

export interface FeatureRow {
  id: string;
  code: string;
  name: string;
  description?: string | null;
  category: FeatureCategory | string;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt?: string;
}

export interface FeatureListResponse {
  data: FeatureRow[];
  total: number;
}

export interface CreateFeatureInput {
  code: string;
  name: string;
  description?: string;
  category: FeatureCategory;
  isActive?: boolean;
  sortOrder?: number;
}

export type UpdateFeatureInput = Partial<CreateFeatureInput>;

const KEY = ["features"] as const;

export const useFeatures = () =>
  useQuery<FeatureListResponse>({
    queryKey: KEY,
    queryFn: () => get<FeatureListResponse>("/admin/features"),
  });

export const useCreateFeature = () => {
  const qc = useQueryClient();
  return useMutation<FeatureRow, Error, CreateFeatureInput>({
    mutationFn: (data) => post<FeatureRow>("/admin/features", data),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
};

export const useUpdateFeature = () => {
  const qc = useQueryClient();
  return useMutation<
    FeatureRow,
    Error,
    { id: string; data: UpdateFeatureInput }
  >({
    mutationFn: ({ id, data }) =>
      put<FeatureRow>(`/admin/features/${id}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY });
      qc.invalidateQueries({ queryKey: ["subscription-plans"] });
    },
  });
};

export const useDeleteFeature = () => {
  const qc = useQueryClient();
  return useMutation<{ success: true }, Error, string>({
    mutationFn: (id) => del<{ success: true }>(`/admin/features/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY });
      qc.invalidateQueries({ queryKey: ["subscription-plans"] });
    },
  });
};
